import {
  Dispatch,
  MutableRefObject,
  ReactElement,
  RefObject,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import L from 'leaflet';
import { MAP_FILTER_SESSION_STORAGE_KEY, MAP_SEARCH_SESSION_STORAGE_KEY } from 'src/features';
import { SessionStorageService } from 'src/features/authentication/utils';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetSites } from 'src/hooks/useGetSites';
import { SITES_MAP_PATH } from 'src/routes/config';
import { NewSite } from 'src/types/sites/sites';

type SiteMapFilterProviderProps = {
  children: ReactElement;
};
type SiteMapFilterContextType = {
  mapFilters: string;
  updateMapFilters: (newFilters: string) => void;
  mapSearch: string;
  updateMapSearch: (newSearch: string) => void;
  mapRef: RefObject<L.Map>;
  markersRef: MutableRefObject<Set<L.Marker>>;
  sites: NewSite[] | [];
  setSites: Dispatch<SetStateAction<NewSite[] | []>>;
  isLoadingSites: boolean;
  isSitesLoaded: boolean;
};

const SiteMapFilterContext = createContext<SiteMapFilterContextType>(
  {} as SiteMapFilterContextType
);

export const SiteMapFilterProvider: React.FunctionComponent<SiteMapFilterProviderProps> = ({
  children,
}: SiteMapFilterProviderProps) => {
  const initalValue = SessionStorageService.getRaw(MAP_FILTER_SESSION_STORAGE_KEY) ?? '';
  const [mapFilters, setMapFilters] = useState<string>(initalValue);
  const [mapSearch, setMapSearch] = useState<string>('');

  const updateMapFilters = (newFilters: string) => {
    SessionStorageService.set(MAP_FILTER_SESSION_STORAGE_KEY, newFilters);
    setMapFilters(newFilters);
  };

  const updateMapSearch = (newSearch: string) => {
    SessionStorageService.set(MAP_SEARCH_SESSION_STORAGE_KEY, newSearch);
    setMapSearch(newSearch);
  };

  // Shared map state so both the header (MapSearch) and the SiteMap page
  // render against the same sites/refs. Fetching is scoped to the map route.
  const mapRef = useRef<L.Map>(null);
  const markersRef = useRef<Set<L.Marker>>(new Set<L.Marker>());

  const { value: customerId } = useAuthentication()?.customerId || {};
  const location = useLocation();
  const isSiteMap = location.pathname === SITES_MAP_PATH;

  const selectedFiltersPayload = useMemo(() => mapFilters?.toUpperCase().split(','), [mapFilters]);

  const {
    sites,
    isLoading: isLoadingSites,
    setSites,
    isLoaded: isSitesLoaded,
  } = useGetSites({
    debouncedSearchTerm: mapSearch,
    customerId,
    siteTypes: selectedFiltersPayload,
    enabled: isSiteMap,
  });

  const contextValue: SiteMapFilterContextType = {
    mapFilters,
    updateMapFilters,
    mapSearch,
    updateMapSearch,
    mapRef,
    markersRef,
    sites,
    setSites,
    isLoadingSites,
    isSitesLoaded,
  };

  return (
    <SiteMapFilterContext.Provider value={contextValue}>{children}</SiteMapFilterContext.Provider>
  );
};

export const useMapFilters = () => useContext(SiteMapFilterContext);
